import type {
  ObjectFieldSchema,
  ObjectProperty,
} from '@carboncopyco/editor-utils';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { FieldPanel } from '../FieldPanel';
import { TableCell } from '../TableCell';
import type { BaseFieldProps } from '../types';
import { useDebouncedValue } from '../useDebouncedValue';

export type ObjectFieldProps = BaseFieldProps<
  ObjectFieldSchema,
  Record<string, unknown>
>;

const columns = [
  {
    accessorKey: 'key',
  },
  {
    accessorKey: 'value',
  },
];

export const ObjectField = ({
  value,
  field,
  readOnly,
  editableField,
  onChangeField,
  onChangeValue,
  onRemoveField,
}: ObjectFieldProps) => {
  const [innerValue, setInnerValue] = useDebouncedValue(value, onChangeValue);

  const data = useMemo(
    () =>
      Object.entries(innerValue).map(([key, value]) => ({
        key,
        value,
      })),
    [innerValue]
  );
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData: ({ row, column, value: cellValue }) => {
        const accessorKey = (column.columnDef as any).accessorKey ?? '';
        const clonedValue = structuredClone(value);
        clonedValue[row.index][accessorKey] = cellValue;
        setInnerValue?.(clonedValue);
      },
    },
  });

  const changePropertyHeader = (
    key: ObjectProperty['key'],
    newValue: ObjectProperty['header']
  ) => {
    const updatedProperties = field.uiSchema.options.properties.map(
      (property) =>
        property.key === key ? { ...property, header: newValue } : property
    );
    onChangeField?.({
      ...field,
      uiSchema: {
        ...field.uiSchema,
        options: {
          ...field.uiSchema.options,
          properties: updatedProperties,
        },
      },
    });
  };

  return (
    <FieldPanel
      field={field}
      editable={editableField}
      onChange={onChangeField}
      onRemoveField={onRemoveField}
    >
      <div className="overflow-auto max-h-[200px] text-ds-natural-600">
        <table className="text-xs rounded-md border border-ds-natural-150 overflow-hidden outline outline-1 outline-ds-natural-150 outline-offset-[-1px]">
          <tbody className="bg-white">
            {table.getRowModel().rows.map((row) => {
              const [keyCell, valueCell] = row.getVisibleCells();
              const key = keyCell.getValue() as string;
              const jsonSchema = field.jsonSchema?.properties?.[key];
              return (
                <tr key={row.id}>
                  <th
                    className="bg-ds-natural-100 text-left font-normal py-1 px-2.5 border border-ds-natural-150"
                    contentEditable={editableField}
                    suppressContentEditableWarning
                    onBlur={(e) => {
                      changePropertyHeader?.(key, e.currentTarget.textContent);
                    }}
                  >
                    {
                      field.uiSchema.options.properties.find(
                        (property) => property.key === key
                      )?.header
                    }
                  </th>
                  <td className="border border-ds-natural-150 py-1 px-2.5">
                    <TableCell
                      value={valueCell.getValue()}
                      onChange={(cellValue) => {
                        const clonedValue = structuredClone(value);
                        clonedValue[key] = cellValue;
                        setInnerValue?.(clonedValue);
                      }}
                      jsonSchema={jsonSchema}
                      readOnly={readOnly}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </FieldPanel>
  );
};
